/**
 * Who is typing, in one line.
 *
 * The element is always rendered, empty or not: a live region has to exist
 * before its contents change, or the change is never announced.
 */

import { useMemo } from 'react'
import type { ReactElement } from 'react'

import { useAuth } from '@/api/auth'
import { useMembers } from '@/api/queries'
import { useTypingUsers } from '@/api/store'
import type { User } from '@/api/types'

function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

function sentence(names: readonly string[]): string {
  const [first, second] = names

  if (first === undefined) return ''
  if (second === undefined) return `${first} is typing…`
  if (names.length === 2) return `${first} and ${second} are typing…`

  const others = names.length - 2
  return `${first}, ${second} and ${others} ${others === 1 ? 'other' : 'others'} are typing…`
}

export interface TypingIndicatorProps {
  roomId: string
  className?: string
}

export function TypingIndicator({ roomId, className }: TypingIndicatorProps): ReactElement {
  const { user } = useAuth()
  const typing = useTypingUsers(roomId)
  const { data: members } = useMembers(roomId)

  const text = useMemo(() => {
    const byId = new Map((members ?? []).map((member) => [member.user.id, member.user]))
    const names = typing
      .filter((id) => id !== user?.id)
      .map((id) => {
        const person = byId.get(id)
        return person === undefined ? 'Someone' : personName(person)
      })
    return sentence(names)
  }, [typing, members, user])

  return (
    <p className={className} aria-live="polite">
      {text}
    </p>
  )
}
